import React from 'react';
import { ArrowRight, Check, Code2, Globe2, HardDrive, ShieldCheck, Sparkles, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const hostingUrl = 'https://961host.net/order/index.php/store/web-hosting';

const plans = [
  { name: 'Business', price: '$5.00', fit: 'Personal websites and blogs', storage: '2 GB SSD storage', email: '3 email accounts', orderUrl: 'https://961host.net/order/cart.php?a=add&pid=171' },
  { name: 'Corporate', price: '$8.00', fit: 'Growing businesses', storage: '5 GB SSD storage', email: '10 email accounts', orderUrl: 'https://961host.net/order/cart.php?a=add&pid=297', featured: true },
  { name: 'Professional', price: '$12.00', fit: 'Power users and agencies', storage: '10 GB SSD storage', email: 'Unlimited email accounts', orderUrl: 'https://961host.net/order/cart.php?a=add&pid=172' },
];

const InfrastructureGraphic = () => (
  <svg viewBox="0 0 620 470" className="h-auto w-full" role="img" aria-label="Abstract website infrastructure illustration">
    <defs>
      <linearGradient id="hero-panel" x1="0" x2="1" y1="0" y2="1">
        <stop stopColor="#1d4ed8" />
        <stop offset="1" stopColor="#2563eb" />
      </linearGradient>
      <linearGradient id="hero-glow" x1="0" x2="1">
        <stop stopColor="#60a5fa" stopOpacity=".32" />
        <stop offset="1" stopColor="#60a5fa" stopOpacity="0" />
      </linearGradient>
    </defs>
    <circle cx="492" cy="92" r="126" fill="url(#hero-glow)" />
    <path d="M60 300 190 194l112 68 138-145" fill="none" stroke="#60a5fa" strokeDasharray="5 10" strokeWidth="2" opacity=".7" />
    <rect x="62" y="119" width="285" height="211" rx="14" fill="#172554" stroke="#60a5fa" strokeOpacity=".4" />
    <path d="M62 154h285" stroke="#60a5fa" strokeOpacity=".32" />
    <circle cx="87" cy="137" r="5" fill="#93c5fd" /><circle cx="105" cy="137" r="5" fill="#60a5fa" opacity=".7" /><circle cx="123" cy="137" r="5" fill="#60a5fa" opacity=".4" />
    <rect x="88" y="181" width="142" height="16" rx="5" fill="#dbeafe" /><rect x="88" y="213" width="205" height="9" rx="4.5" fill="#93c5fd" opacity=".75" /><rect x="88" y="238" width="174" height="9" rx="4.5" fill="#93c5fd" opacity=".48" />
    <rect x="88" y="278" width="94" height="25" rx="6" fill="url(#hero-panel)" />
    <rect x="382" y="155" width="165" height="196" rx="16" fill="#0f1d40" stroke="#60a5fa" strokeOpacity=".45" />
    <rect x="410" y="189" width="109" height="28" rx="7" fill="#1e3a8a" /><rect x="410" y="233" width="109" height="28" rx="7" fill="#1e3a8a" /><rect x="410" y="277" width="109" height="28" rx="7" fill="#1e3a8a" />
    <circle cx="429" cy="203" r="5" fill="#60a5fa" /><circle cx="429" cy="247" r="5" fill="#60a5fa" /><circle cx="429" cy="291" r="5" fill="#60a5fa" />
    <path d="M347 223h35m-35 59h35" stroke="#60a5fa" strokeWidth="2" />
    <circle cx="190" cy="194" r="8" fill="#dbeafe" /><circle cx="302" cy="262" r="8" fill="#dbeafe" />
  </svg>
);

const services = [
  { icon: Globe2, title: 'Web Hosting', copy: 'Reliable shared hosting for business sites, portfolios, stores, and the projects still taking shape.', to: '/hosting', link: 'Explore hosting' },
  { icon: Zap, title: 'WordPress Hosting', copy: 'Launch with WordPress, manage it through cPanel, and keep your publishing process simple.', to: '/wordpress', link: 'Explore WordPress' },
  { icon: Code2, title: 'Hosting for Builders', copy: 'A practical place to deploy Node.js and Python projects when you are ready to put them online.', to: '/vibecoders', link: 'Explore VibeCoders' },
];

export const HomePage = () => (
  <main className="overflow-hidden pt-16">
    <section className="relative isolate overflow-hidden bg-foreground">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(37,99,235,0.32),transparent_28%),radial-gradient(circle_at_20%_100%,rgba(30,64,175,0.3),transparent_35%)]" />
      <div className="relative mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-6 sm:py-24 lg:grid-cols-[1fr_.9fr] lg:items-center lg:gap-16 lg:px-8 lg:py-28">
        <div>
          <p className="text-sm font-medium text-blue-200">Hosting made straightforward</p>
          <h1 className="mt-5 max-w-3xl text-4xl font-semibold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
            A dependable home for every kind of website.
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300">
            From your first website to your next application, 961Host gives you the tools, performance, and human support to launch with confidence.
          </p>
          <div className="mt-9 flex flex-col gap-3 sm:flex-row">
            <Button size="lg" variant="primary-light" asChild>
              <a href={hostingUrl} target="_blank" rel="noopener noreferrer">View hosting plans <ArrowRight className="ml-2 h-5 w-5" /></a>
            </Button>
            <Button size="lg" variant="outline-dark" asChild>
              <Link to="/contact">Talk to our team</Link>
            </Button>
          </div>
          <div className="mt-12 flex flex-wrap gap-x-7 gap-y-3 text-sm text-slate-300">
            {['SSD storage', 'Free SSL certificates', 'cPanel included'].map((item) => <span key={item} className="flex items-center gap-2"><Check className="h-4 w-4 text-blue-300" />{item}</span>)}
          </div>
        </div>
        <div className="relative mx-auto w-full max-w-xl lg:max-w-none"><InfrastructureGraphic /></div>
      </div>
    </section>

    <section className="bg-secondary/50 py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col justify-between gap-5 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold text-primary">Shared hosting plans</p>
            <h2 className="mt-3 text-3xl font-semibold text-foreground sm:text-4xl">Choose the plan that matches your next site.</h2>
            <p className="mt-4 text-lg leading-8 text-muted-foreground">Every plan includes unlimited bandwidth, cPanel, free Let's Encrypt SSL, and a practical foundation for a website that needs to be online.</p>
          </div>
          <Link to="/hosting" className="inline-flex shrink-0 items-center gap-2 text-sm font-semibold text-primary hover:text-primary-hover">Compare every feature <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          {plans.map((plan) => (
            <Card key={plan.name} className={`card-premium relative flex flex-col ${plan.featured ? 'border-2 border-primary shadow-md' : ''}`}>
              <CardContent className="flex flex-1 flex-col p-7">
                {plan.featured && <span className="mb-5 inline-flex w-fit rounded-full bg-primary px-3 py-1 text-xs font-semibold text-white">Best value</span>}
                <h3 className="text-xl font-semibold text-foreground">{plan.name}</h3>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">{plan.fit}</p>
                <p className="mt-6 text-4xl font-semibold tracking-tight text-foreground">{plan.price}<span className="ml-1 text-base font-normal text-muted-foreground">/mo</span></p>
                <ul className="mt-7 flex-1 space-y-3 text-sm text-foreground">
                  {[plan.storage, plan.email, 'Free SSL certificate', 'cPanel control panel'].map((feature) => <li key={feature} className="flex items-center gap-2"><Check className="h-4 w-4 shrink-0 text-primary" />{feature}</li>)}
                </ul>
                <Button className="mt-8 w-full" variant={plan.featured ? 'default' : 'outline'} asChild><a href={plan.orderUrl} target="_blank" rel="noopener noreferrer">Choose {plan.name} <ArrowRight className="ml-2 h-4 w-4" /></a></Button>
              </CardContent>
            </Card>
          ))}
        </div>
        <p className="mt-6 text-center text-sm text-muted-foreground">Free domain name and privacy protection are available with annual hosting plans.</p>
      </div>
    </section>

    <section className="bg-white py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold text-primary">Start with what you need</p>
          <h2 className="mt-3 text-3xl font-semibold text-foreground sm:text-4xl">Hosting built around the way you work.</h2>
          <p className="mt-4 text-lg leading-7 text-muted-foreground">Choose a clear path today, with room to grow when your plans do.</p>
        </div>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {services.map(({ icon: Icon, title, copy, to, link }) => (
            <Card key={title} className="card-premium flex flex-col">
              <CardContent className="flex flex-1 flex-col p-7">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10"><Icon className="h-6 w-6 text-primary" /></div>
                <h3 className="mt-6 text-xl font-semibold text-foreground">{title}</h3>
                <p className="mt-3 flex-1 leading-7 text-muted-foreground">{copy}</p>
                <Link to={to} className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary-hover">{link} <ArrowRight className="h-4 w-4" /></Link>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-white py-20 sm:py-24">
      <div className="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
        <div className="rounded-2xl border border-border bg-slate-50 p-7 sm:p-9">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10"><Globe2 className="h-6 w-6 text-primary" /></div>
          <p className="mt-6 text-sm font-semibold text-primary">For WordPress sites</p>
          <h2 className="mt-3 text-2xl font-semibold text-foreground">Publish without a complicated setup.</h2>
          <p className="mt-4 leading-7 text-muted-foreground">Install WordPress through Softaculous, then manage files, databases, domains, and email from cPanel. It is a clear path for blogs, business websites, and stores.</p>
          <Link to="/wordpress" className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary-hover">Explore WordPress hosting <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="rounded-2xl bg-foreground p-7 sm:p-9">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10"><Code2 className="h-6 w-6 text-blue-200" /></div>
          <p className="mt-6 text-sm font-semibold text-blue-200">For builders</p>
          <h2 className="mt-3 text-2xl font-semibold text-white">Give your project a real home on the web.</h2>
          <p className="mt-4 leading-7 text-slate-300">Move from an idea to a live project with a hosting path built for developers working with Node.js and Python.</p>
          <Link to="/vibecoders" className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-blue-200 hover:text-white">Explore VibeCoders <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </div>
    </section>

    <section className="bg-secondary/50 py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold text-primary">Getting started</p>
          <h2 className="mt-3 text-3xl font-semibold text-foreground sm:text-4xl">A simple route from plan to published.</h2>
          <p className="mt-4 text-lg leading-8 text-muted-foreground">Choose the capacity you need, set up your site with familiar tools, and contact us when you want help selecting a path.</p>
        </div>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {[
            ['01', 'Choose your plan', 'Start with Business for a personal site, Corporate for a growing business, or Professional for more capacity.'],
            ['02', 'Set up your website', 'Use cPanel to manage your account and Softaculous to install WordPress and other applications.'],
            ['03', 'Launch with support', 'Point your domain, publish your site, and reach out to our team whenever you need guidance.'],
          ].map(([number, title, copy]) => <div key={number} className="border-t-2 border-primary pt-5"><p className="text-sm font-semibold text-primary">{number}</p><h3 className="mt-3 text-xl font-semibold text-foreground">{title}</h3><p className="mt-3 leading-7 text-muted-foreground">{copy}</p></div>)}
        </div>
      </div>
    </section>

    <section className="bg-secondary/50 py-20 sm:py-24">
      <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[.9fr_1.1fr] lg:items-center lg:gap-20 lg:px-8">
        <div>
          <p className="text-sm font-semibold text-primary">The essentials, included</p>
          <h2 className="mt-3 text-3xl font-semibold text-foreground sm:text-4xl">More focus on your site. Less time on its setup.</h2>
          <p className="mt-5 max-w-xl text-lg leading-8 text-muted-foreground">A capable hosting foundation should be easy to understand and ready for the day-to-day work behind your website.</p>
          <Button className="mt-8" size="lg" asChild><Link to="/features">See all hosting features <ArrowRight className="ml-2 h-5 w-5" /></Link></Button>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {[
            [HardDrive, 'Fast storage', 'SSD storage gives your website a responsive foundation.'],
            [ShieldCheck, 'Security included', 'Free SSL certificates help keep every visitor connection protected.'],
            [Globe2, 'Easy management', 'Use cPanel to manage files, domains, email, and databases in one place.'],
            [Zap, 'Ready for WordPress', 'Install WordPress and other popular applications with Softaculous.'],
          ].map(([Icon, title, copy]) => (
            <div key={title} className="rounded-lg border border-border bg-white p-6 shadow-sm">
              <Icon className="h-6 w-6 text-primary" />
              <h3 className="mt-4 font-semibold text-foreground">{title}</h3>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">{copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-foreground px-4 py-20 text-center sm:px-6 sm:py-24 lg:px-8">
      <div className="mx-auto max-w-3xl">
        <Sparkles className="mx-auto h-9 w-9 text-blue-200" />
        <h2 className="mt-5 text-3xl font-semibold text-white sm:text-4xl">Ready to put your website online?</h2>
        <p className="mt-4 text-lg leading-8 text-slate-300">Find a hosting plan that fits today, or speak with our team about the right place to begin.</p>
        <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
          <Button size="lg" variant="primary-light" asChild><a href={hostingUrl} target="_blank" rel="noopener noreferrer">Browse hosting plans <ArrowRight className="ml-2 h-5 w-5" /></a></Button>
          <Button size="lg" variant="outline-dark" asChild><Link to="/contact">Contact our team</Link></Button>
        </div>
      </div>
    </section>
  </main>
);
